import type { Dispatch } from 'react';
import {
  AccessRuleAction,
  AccessRuleStateType,
  AssetAction,
  AssetManagerAction,
  AssetManagerStateType,
  AssetStateType,
  AssetUpdateAction,
  AssetUpdateStateType,
  ClientCacheAction,
  ClientCacheStateType,
  ConfigurationAction,
  ConfigurationStateType,
  DeviceStateType,
  DigitalTwinAction,
  DigitalTwinStateType,
  FeatureStateType,
  FileAction,
  LayerAction,
  LayerStateType,
  LayoutAction,
  LayoutStateType,
  PlanStateType,
  PlannedTwinIDUpdateStateType,
  ReportAction,
  StatusUpdateAction,
  StatusUpdateStateType,
  UserAction,
  VisibilityFilterAction,
  VisibilityFilterStateType,
  useAccessRule,
  useAsset,
  useAssetManager,
  useAssetUpdate,
  useClientCache,
  useConfiguration,
  useDevice,
  useDigitalTwin,
  useFeature,
  useFile,
  useLayer,
  useLayout,
  usePlan,
  usePlannedTwinIDUpdate,
  useReport,
  useStatusUpdate,
  useUser,
  useVisibilityFilter,
} from '.';

export type ContextStores = {
  clientCacheState: ClientCacheStateType
  layerState: LayerStateType
  assetManagerState: AssetManagerStateType
  visibilityFilterState: VisibilityFilterStateType
  assetUpdateState: AssetUpdateStateType
  statusUpdateState: StatusUpdateStateType
  plannedTwinIDUpdateState: PlannedTwinIDUpdateStateType
  assetState: AssetStateType
  deviceState: DeviceStateType
  planState: PlanStateType
  accessRuleState: AccessRuleStateType
  digitalTwinState: DigitalTwinStateType
  configurationState: ConfigurationStateType
  featureState: FeatureStateType
  layoutState: LayoutStateType
  clientCacheDispatch: Dispatch<ClientCacheAction>
  layerDispatch: Dispatch<LayerAction>
  userDispatch: Dispatch<UserAction>
  assetDispatch: Dispatch<AssetAction>
  assetManagerDispatch: Dispatch<AssetManagerAction>
  accessRuleDispatch: Dispatch<AccessRuleAction>
  fileDispatch: Dispatch<FileAction>
  assetUpdateDispatch: Dispatch<AssetUpdateAction>
  statusUpdateDispatch: Dispatch<StatusUpdateAction>
  visibilityFilterDispatch: Dispatch<VisibilityFilterAction>
  digitalTwinDispatch: Dispatch<DigitalTwinAction>
  configurationDispatch: Dispatch<ConfigurationAction>
  layoutDispatch: Dispatch<LayoutAction>
  reportDispatch: Dispatch<ReportAction>
};

export const getContextStores = () => ({
  get clientCacheState() {
    const [clientCacheState] = useClientCache();
    return clientCacheState;
  },
  get layerState() {
    const [layerState] = useLayer();
    return layerState;
  },
  get userState() {
    const [userState] = useUser();
    return userState;
  },
  get deviceState() {
    const [deviceState] = useDevice();
    return deviceState;
  },
  get planState() {
    const [planState] = usePlan();
    return planState;
  },
  get assetManagerState() {
    const [assetManagerState] = useAssetManager();
    return assetManagerState;
  },
  get assetState() {
    const [assetState] = useAsset();
    return assetState;
  },
  get accessRuleState() {
    const [accessRuleState] = useAccessRule();
    return accessRuleState;
  },
  get fileState() {
    const [fileState] = useFile();
    return fileState;
  },
  get assetUpdateState() {
    const [assetUpdateState] = useAssetUpdate();
    return assetUpdateState;
  },
  get statusUpdateState() {
    const [statusUpdateState] = useStatusUpdate();
    return statusUpdateState;
  },
  get plannedTwinIDUpdateState() {
    const [plannedTwinIDUpdateState] = usePlannedTwinIDUpdate();
    return plannedTwinIDUpdateState;
  },
  get visibilityFilterState() {
    const [visibilityFilterState] = useVisibilityFilter();
    return visibilityFilterState;
  },
  get digitalTwinState() {
    const [digitalTwinState] = useDigitalTwin();
    return digitalTwinState;
  },
  get layoutState() {
    const [layoutState] = useLayout();
    return layoutState;
  },
  get configurationState() {
    const [configurationState] = useConfiguration();
    return configurationState;
  },
  get featureState() {
    const [featureState] = useFeature();
    return featureState;
  },
  get reportState() {
    const [reportState] = useReport();
    return reportState;
  },
  get clientCacheDispatch() {
    const [, clientCacheDispatch] = useClientCache();
    return clientCacheDispatch;
  },
  get layerDispatch() {
    const [, layerDispatch] = useLayer();
    return layerDispatch;
  },
  get deviceDispatch() {
    const [, deviceDispatch] = useDevice();
    return deviceDispatch;
  },
  get planDispatch() {
    const [, planDispatch] = usePlan();
    return planDispatch;
  },
  get userDispatch() {
    const [, userDispatch] = useUser();
    return userDispatch;
  },
  get assetManagerDispatch() {
    const [, assetManagerDispatch] = useAssetManager();
    return assetManagerDispatch;
  },
  get assetDispatch() {
    const [, assetDispatch] = useAsset();
    return assetDispatch;
  },
  get accessRuleDispatch() {
    const [, accessRuleDispatch] = useAccessRule();
    return accessRuleDispatch;
  },
  get fileDispatch() {
    const [, fileDispatch] = useFile();
    return fileDispatch;
  },
  get assetUpdateDispatch() {
    const [, assetUpdateDispatch] = useAssetUpdate();
    return assetUpdateDispatch;
  },
  get statusUpdateDispatch() {
    const [, statusUpdateDispatch] = useStatusUpdate();
    return statusUpdateDispatch;
  },
  get plannedTwinIDUpdateDispatch() {
    const [, plannedTwinIDUpdateDispatch] = usePlannedTwinIDUpdate();
    return plannedTwinIDUpdateDispatch;
  },
  get digitalTwinDispatch() {
    const [, digitalTwinDispatch] = useDigitalTwin();
    return digitalTwinDispatch;
  },
  get visibilityFilterDispatch() {
    const [, visibilityFilterDispatch] = useVisibilityFilter();
    return visibilityFilterDispatch;
  },
  get layoutDispatch() {
    const [, layoutDispatch] = useLayout();
    return layoutDispatch;
  },
  get configurationDispatch() {
    const [, configurationDispatch] = useConfiguration();
    return configurationDispatch;
  },
  get reportDispatch() {
    const [, reportDispatch] = useReport();
    return reportDispatch;
  },
});
